import React, { memo, useMemo, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";
import { useAnnouncements } from "../../services/announcementService";
import { Loader2, AlertCircle } from "lucide-react";

interface AnnouncementsProps {
  title?: string;
  maxItems?: number;
}

/**
 * Modern Announcements component with gradient backgrounds, floating cards, and sleek design
 */
const Announcements: React.FC<AnnouncementsProps> = memo(
  ({ title, maxItems = 4 }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { currentLanguage } = useSelector(
      (state: RootState) => state.language
    );
    const [isLoaded, setIsLoaded] = useState(false);

    // Fetch latest announcements from API
    const {
      data: announcementsResponse,
      isLoading,
      error,
    } = useAnnouncements({
      latest: true,
      per_page: maxItems,
      locale: currentLanguage,
    });

    const displayAnnouncements = useMemo(() => {
      if (!announcementsResponse?.announcements) return [];
      return announcementsResponse.announcements;
    }, [announcementsResponse]);

    useEffect(() => {
      if (!isLoading) {
        setIsLoaded(true);
      }
    }, [isLoading]);

    // Loading state
    if (isLoading) {
      return (
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-white via-emerald-50/50 to-stone-100/50 dark:from-gray-900 dark:via-emerald-900/10 dark:to-stone-900/10">
          <div className="relative z-10 max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-6">
                {title || t("announcements.title", "Announcements")}
              </h2>
            </div>
            <div className="flex justify-center items-center py-16">
              <Loader2 className="w-12 h-12 text-emerald-600 animate-spin" />
            </div>
          </div>
        </section>
      );
    }

    // Error state
    if (error) {
      return (
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-white via-emerald-50/50 to-stone-100/50 dark:from-gray-900 dark:via-emerald-900/10 dark:to-stone-900/10">
          <div className="relative z-10 max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-6">
                {title || t("announcements.title", "Announcements")}
              </h2>
            </div>
            <div className="flex flex-col items-center justify-center py-16">
              <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {t("announcements.error", "Failed to load announcements")}
              </p>
            </div>
          </div>
        </section>
      );
    }

    // Empty state - show section even if no announcements (for better UX)
    // The section will still display with a message if needed
    if (!displayAnnouncements || displayAnnouncements.length === 0) {
      // Still show the section header but no announcements
      return (
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-white via-emerald-50/50 to-stone-100/50 dark:from-gray-900 dark:via-emerald-900/10 dark:to-stone-900/10">
          <div className="relative z-10 max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-20"
            >
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={
                  isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
                }
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight tracking-tight"
              >
                {title || t("announcements.title", "Announcements")}
                <span className="block mt-2 bg-gradient-to-r from-emerald-600 via-stone-500 to-emerald-700 bg-clip-text text-transparent">
                  {t("announcements.titleAccent", "Stay Informed")}
                </span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={
                  isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
                }
                transition={{ duration: 0.8, delay: 0.5 }}
                className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto"
              >
                {t(
                  "announcements.no_announcements_available",
                  "No announcements available at this time. Check back soon for updates!"
                )}
              </motion.p>
            </motion.div>
          </div>
        </section>
      );
    }

    return (
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-white via-emerald-50/50 to-stone-100/50 dark:from-gray-900 dark:via-emerald-900/10 dark:to-stone-900/10">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-200/20 dark:bg-emerald-900/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-stone-200/20 dark:bg-stone-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={
                isLoaded ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }
              }
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-block mb-6"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-emerald-800 dark:text-emerald-300 rounded-full text-sm font-semibold border border-emerald-200 dark:border-emerald-800 shadow-lg">
                <span className="text-lg">📢</span>
                <span>{t("announcements.badge", "Latest News")}</span>
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight tracking-tight"
            >
              {title || t("announcements.title", "Announcements")}
              <span className="block mt-2 bg-gradient-to-r from-emerald-600 via-stone-500 to-emerald-700 bg-clip-text text-transparent">
                {t("announcements.titleAccent", "Stay Informed")}
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto"
            >
              {t(
                "announcements.subtitle",
                "Stay updated with our latest community announcements and important updates."
              )}
            </motion.p>
          </motion.div>

          {/* Announcements Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
            {displayAnnouncements.map((announcement, index) => (
              <motion.article
                key={announcement.id}
                initial={{ opacity: 0, y: 50 }}
                animate={
                  isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }
                }
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                whileHover={{ y: -12, scale: 1.03 }}
                className="group relative"
              >
                {/* Floating gradient glow */}
                <div
                  className={`absolute -inset-1 rounded-3xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500 ${announcement.is_pinned
                      ? "bg-gradient-to-r from-emerald-500 to-stone-500"
                      : "bg-gradient-to-r from-emerald-500 to-stone-400"
                    }`}
                ></div>

                {/* Card */}
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 overflow-hidden h-full flex flex-col">
                  {/* Pinned Indicator */}
                  {announcement.is_pinned && (
                    <div className="px-4 py-3 bg-gradient-to-r from-emerald-600 to-stone-600">
                      <span className="text-xs font-bold text-white tracking-wide">
                        📌 PINNED
                      </span>
                    </div>
                  )}

                  <div className="p-6 lg:p-8 flex-1 flex flex-col">
                    {/* Title */}
                    <h3 className="text-lg lg:text-xl font-extrabold text-gray-900 dark:text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-emerald-600 group-hover:to-stone-600 transition-all duration-300 line-clamp-2">
                      {announcement.title}
                    </h3>

                    {/* Body/Description */}
                    <p className="text-sm lg:text-base text-gray-600 dark:text-gray-300 mb-6 leading-relaxed line-clamp-3 flex-1">
                      {announcement.body}
                    </p>

                    {/* Date and Read More */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700 mt-auto">
                      <time className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                        {announcement.created_at
                          ? new Date(
                            announcement.created_at
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                          : ""}
                      </time>

                      {/* Read More Button */}
                      <motion.button
                        onClick={() =>
                          navigate(
                            `/${currentLanguage}/announcements/${announcement.id}`
                          )
                        }
                        whileHover={{ scale: 1.05, x: 4 }}
                        whileTap={{ scale: 0.95 }}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-bold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/40 rounded-xl transition-all duration-200 group/btn"
                      >
                        {t("announcements.readMore", "Read More")}
                        <svg
                          className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-200"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </motion.button>
                    </div>
                  </div>

                  {/* Decorative gradient corner */}
                  <div
                    className={`absolute -bottom-8 -right-8 w-24 h-24 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-500 blur-2xl ${announcement.is_pinned
                        ? "bg-gradient-to-br from-emerald-500 to-stone-500"
                        : "bg-gradient-to-br from-emerald-500 to-stone-500"
                      }`}
                  ></div>
                </div>
              </motion.article>
            ))}
          </div>

          {/* View All Button */}
          {displayAnnouncements.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="text-center mt-16"
            >
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(`/${currentLanguage}/announcements`)}
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-800 text-gray-900 dark:text-white rounded-2xl font-bold text-lg transition-all duration-200 shadow-xl hover:shadow-2xl border-2 border-gray-200 dark:border-gray-700"
              >
                {t("announcements.viewAll", "View All Announcements")}
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </motion.button>
            </motion.div>
          )}
        </div>
      </section>
    );
  }
);

// Display name for debugging
Announcements.displayName = "Announcements";

export default Announcements;
